/**
 * AdvertiserPayoutRulesPage — CRUD list for advertiser payout rules.
 *
 * Layout ported from pre-nuke legacy `apps/dash-legacy/src/routes/_authed/rules/index.tsx`
 * (RulesPage), modernized:
 *   - dropped shadcn/ui, lucide-react, zod, @tanstack/react-router imports
 *   - plain Tailwind per scaffold-dash-feature.sh contract (D6-clean: fetch only via ./api.ts)
 *   - emerald -> sky gradient H1, zinc borders, rounded-md/lg, light theme
 *   - table columns: Advertiser | Rule name | Condition | Action | Effective window | Status
 *   - "New Rule" drawer (right-side slide-over) with advertiser picker +
 *     condition builder (metric + operator + value) + action selector +
 *     date range (effective_from / effective_to)
 *   - binds only to /api/v1/advertiser-payout-rules (+ /api/v1/advertisers for picker)
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAdvertiserPayoutRulesList,
	useAdvertiserPayoutRulesCreate,
	useAdvertiserPayoutRulesDelete,
	useAdvertisersList,
} from "./hooks";
import type {
	AdvertiserPayoutRulesRow,
	CreateAdvertiserPayoutRulesBody,
	PayoutRuleActionKind,
	PayoutRuleMetric,
	PayoutRuleOperator,
	PayoutRuleStatus,
} from "./api";

const CARD = "rounded-lg border border-zinc-200 bg-white shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 disabled:cursor-not-allowed disabled:bg-zinc-50";
const SELECT = INPUT + " appearance-none";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-md border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center gap-1.5 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST_DANGER =
	"inline-flex items-center gap-1 rounded-md border border-transparent px-2 py-1 text-xs font-medium text-zinc-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60";

const METRICS: { value: PayoutRuleMetric; label: string }[] = [
	{ value: "clicks", label: "Clicks" },
	{ value: "conversions", label: "Conversions" },
	{ value: "payout", label: "Payout" },
	{ value: "epc", label: "EPC" },
	{ value: "cr", label: "Conversion rate" },
	{ value: "ctr", label: "Click-through rate" },
];

const OPERATORS: { value: PayoutRuleOperator; label: string }[] = [
	{ value: ">", label: ">" },
	{ value: ">=", label: ">=" },
	{ value: "<", label: "<" },
	{ value: "<=", label: "<=" },
	{ value: "=", label: "=" },
	{ value: "!=", label: "!=" },
];

const ACTION_KINDS: { value: PayoutRuleActionKind; label: string; hint: string }[] = [
	{ value: "multiplier", label: "Multiplier", hint: "e.g. 1.10 = +10% payout" },
	{ value: "flat", label: "Flat amount", hint: "absolute payout in USD" },
	{ value: "cap", label: "Cap", hint: "max payout in USD" },
];

interface DraftForm {
	advertiser_id: string;
	name: string;
	metric: PayoutRuleMetric;
	operator: PayoutRuleOperator;
	value: string;
	action_kind: PayoutRuleActionKind;
	action_amount: string;
	effective_from: string;
	effective_to: string;
	status: PayoutRuleStatus;
}

const blankDraft = (): DraftForm => ({
	advertiser_id: "",
	name: "",
	metric: "conversions",
	operator: ">=",
	value: "",
	action_kind: "multiplier",
	action_amount: "1.1",
	effective_from: "",
	effective_to: "",
	status: "active",
});

function formatWindow(from: string | null, to: string | null): string {
	if (!from && !to) return "Always";
	if (from && to) return `${from} \u2192 ${to}`;
	if (from) return `From ${from}`;
	return `Until ${to}`;
}

function formatAction(action: AdvertiserPayoutRulesRow["action"]): string {
	if (!action) return "-";
	if (action.kind === "multiplier") return `x${action.amount}`;
	if (action.kind === "flat") return `$${action.amount}`;
	return `cap $${action.amount}`;
}

function formatCondition(c: AdvertiserPayoutRulesRow["condition"]): string {
	if (!c) return "-";
	const metric = METRICS.find((m) => m.value === c.metric)?.label ?? c.metric;
	return `${metric} ${c.operator} ${c.value}`;
}

export function AdvertiserPayoutRulesPage() {
	const listQuery = useAdvertiserPayoutRulesList();
	const advertisers = useAdvertisersList();
	const createMut = useAdvertiserPayoutRulesCreate();
	const deleteMut = useAdvertiserPayoutRulesDelete();

	const [drawerOpen, setDrawerOpen] = useState(false);
	const [draft, setDraft] = useState<DraftForm>(blankDraft());

	const items = listQuery.data?.items ?? [];
	const advertiserOptions = advertisers.data ?? [];
	const advertiserLookup = useMemo(() => {
		const m = new Map<string, string>();
		for (const a of advertiserOptions) m.set(a.id, a.name);
		return m;
	}, [advertiserOptions]);

	const canSubmit =
		!!draft.advertiser_id.trim() &&
		!!draft.name.trim() &&
		draft.value.trim() !== "" &&
		Number.isFinite(Number(draft.value)) &&
		draft.action_amount.trim() !== "" &&
		Number.isFinite(Number(draft.action_amount));

	const handleOpenDrawer = () => {
		setDraft(blankDraft());
		createMut.reset();
		setDrawerOpen(true);
	};

	const handleCloseDrawer = () => {
		if (createMut.isPending) return;
		setDrawerOpen(false);
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (!canSubmit) return;
		const body: CreateAdvertiserPayoutRulesBody = {
			advertiser_id: draft.advertiser_id.trim(),
			name: draft.name.trim(),
			condition: {
				metric: draft.metric,
				operator: draft.operator,
				value: Number(draft.value),
			},
			action: {
				kind: draft.action_kind,
				amount: Number(draft.action_amount),
			},
			effective_from: draft.effective_from ? draft.effective_from : null,
			effective_to: draft.effective_to ? draft.effective_to : null,
			status: draft.status,
		};
		createMut.mutate(body, {
			onSuccess: () => {
				setDrawerOpen(false);
				setDraft(blankDraft());
			},
		});
	};

	return (
		<main
			data-testid="advertiser-payout-rules-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-2">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						advertiser-payout-rules
					</p>
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Advertiser Payout Rules
					</h1>
					<p className="max-w-2xl text-sm text-zinc-500">
						Automated payout adjustments per advertiser. Trigger on traffic metrics
						(conversions, EPC, CR) and apply a multiplier, flat amount, or cap.
					</p>
				</div>
				<div className="flex items-center gap-3">
					<span className="text-xs text-zinc-500" data-testid="advertiser-payout-rules-count">
						{items.length} rule{items.length === 1 ? "" : "s"}
					</span>
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={handleOpenDrawer}
						data-testid="advertiser-payout-rules-new"
					>
						New Rule
					</button>
				</div>
			</header>

			<section className={CARD} aria-label="Advertiser payout rules list">
				{listQuery.isLoading ? (
					<p
						className="p-8 text-sm text-zinc-500"
						data-testid="advertiser-payout-rules-loading"
					>
						Loading rules.
					</p>
				) : listQuery.error ? (
					<p className="p-8 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<div
						className="flex flex-col items-center justify-center gap-2 p-16 text-center"
						data-testid="advertiser-payout-rules-empty"
					>
						<p className="text-sm font-medium text-zinc-700">No payout rules yet</p>
						<p className="max-w-md text-xs text-zinc-500">
							Create a rule to automatically adjust an advertiser&apos;s payout when a
							metric crosses a threshold.
						</p>
						<button
							type="button"
							className={BTN_SECONDARY + " mt-2"}
							onClick={handleOpenDrawer}
						>
							New Rule
						</button>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full text-sm"
							data-testid="advertiser-payout-rules-list"
						>
							<thead className="border-b border-zinc-200 bg-zinc-50 text-left">
								<tr>
									<th className="px-4 py-2.5 font-medium text-zinc-600">Advertiser</th>
									<th className="px-4 py-2.5 font-medium text-zinc-600">Rule name</th>
									<th className="px-4 py-2.5 font-medium text-zinc-600">Condition</th>
									<th className="px-4 py-2.5 font-medium text-zinc-600">Action</th>
									<th className="px-4 py-2.5 font-medium text-zinc-600">Effective window</th>
									<th className="px-4 py-2.5 font-medium text-zinc-600">Status</th>
									<th className="px-4 py-2.5" />
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{items.map((rule) => {
									const advertiserName =
										rule.advertiser_name ||
										advertiserLookup.get(rule.advertiser_id) ||
										rule.advertiser_id ||
										"-";
									return (
										<tr
											key={rule.id}
											className="hover:bg-zinc-50/60"
											data-testid={`advertiser-payout-rules-row-${rule.id}`}
										>
											<td className="px-4 py-3 font-medium text-zinc-900">
												{advertiserName}
											</td>
											<td className="px-4 py-3 text-zinc-700">{rule.name}</td>
											<td className="px-4 py-3 text-zinc-600">
												{formatCondition(rule.condition)}
											</td>
											<td className="px-4 py-3">
												<span className="rounded-md border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
													{formatAction(rule.action)}
												</span>
											</td>
											<td className="px-4 py-3 text-xs text-zinc-500">
												{formatWindow(rule.effective_from, rule.effective_to)}
											</td>
											<td className="px-4 py-3">
												{rule.status === "active" ? (
													<span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
														Active
													</span>
												) : (
													<span className="rounded-md border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs font-medium text-zinc-600">
														Paused
													</span>
												)}
											</td>
											<td className="px-4 py-3 text-right">
												<button
													type="button"
													className={BTN_GHOST_DANGER}
													onClick={() => deleteMut.mutate(rule.id)}
													disabled={deleteMut.isPending}
													data-testid={`advertiser-payout-rules-delete-${rule.id}`}
												>
													Delete
												</button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{drawerOpen ? (
				<div
					className="fixed inset-0 z-40 flex"
					role="dialog"
					aria-modal="true"
					aria-label="New advertiser payout rule"
					data-testid="advertiser-payout-rules-drawer"
				>
					<div
						className="flex-1 bg-zinc-900/40"
						onClick={handleCloseDrawer}
						aria-hidden="true"
					/>
					<aside className="flex h-full w-full max-w-md flex-col border-l border-zinc-200 bg-white shadow-xl">
						<div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
							<div>
								<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
									advertiser-payout-rules
								</p>
								<h2 className="text-lg font-semibold text-zinc-900">New Rule</h2>
							</div>
							<button
								type="button"
								className={BTN_SECONDARY}
								onClick={handleCloseDrawer}
								disabled={createMut.isPending}
								aria-label="Close"
							>
								Close
							</button>
						</div>
						<form
							onSubmit={handleSubmit}
							className="flex flex-1 flex-col gap-5 overflow-y-auto px-5 py-5"
							data-testid="advertiser-payout-rules-create-form"
						>
							<div className="flex flex-col gap-1.5">
								<label
									htmlFor="advertiser-picker"
									className="text-xs font-medium text-zinc-700"
								>
									Advertiser
								</label>
								<select
									id="advertiser-picker"
									className={SELECT}
									value={draft.advertiser_id}
									onChange={(e) =>
										setDraft((d) => ({ ...d, advertiser_id: e.currentTarget.value }))
									}
									disabled={createMut.isPending}
									data-testid="advertiser-payout-rules-advertiser-select"
								>
									<option value="">Select advertiser.</option>
									{advertiserOptions.map((a) => (
										<option key={a.id} value={a.id}>
											{a.name}
										</option>
									))}
								</select>
								{advertisers.isLoading ? (
									<p className="text-[11px] text-zinc-400">Loading advertisers.</p>
								) : advertiserOptions.length === 0 ? (
									<input
										type="text"
										className={INPUT}
										placeholder="or paste advertiser id"
										value={draft.advertiser_id}
										onChange={(e) =>
											setDraft((d) => ({ ...d, advertiser_id: e.currentTarget.value }))
										}
										disabled={createMut.isPending}
										data-testid="advertiser-payout-rules-advertiser-input"
									/>
								) : null}
							</div>

							<div className="flex flex-col gap-1.5">
								<label htmlFor="rule-name" className="text-xs font-medium text-zinc-700">
									Rule name
								</label>
								<input
									id="rule-name"
									type="text"
									className={INPUT}
									placeholder="e.g. High-CR payout boost"
									value={draft.name}
									onChange={(e) =>
										setDraft((d) => ({ ...d, name: e.currentTarget.value }))
									}
									disabled={createMut.isPending}
									data-testid="advertiser-payout-rules-name-input"
								/>
							</div>

							<div className="flex flex-col gap-2 rounded-md border border-zinc-200 bg-zinc-50/60 p-3">
								<p className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
									Condition
								</p>
								<div className="grid grid-cols-3 gap-2">
									<select
										className={SELECT}
										value={draft.metric}
										onChange={(e) =>
											setDraft((d) => ({
												...d,
												metric: e.currentTarget.value as PayoutRuleMetric,
											}))
										}
										disabled={createMut.isPending}
										aria-label="Metric"
										data-testid="advertiser-payout-rules-metric"
									>
										{METRICS.map((m) => (
											<option key={m.value} value={m.value}>
												{m.label}
											</option>
										))}
									</select>
									<select
										className={SELECT}
										value={draft.operator}
										onChange={(e) =>
											setDraft((d) => ({
												...d,
												operator: e.currentTarget.value as PayoutRuleOperator,
											}))
										}
										disabled={createMut.isPending}
										aria-label="Operator"
										data-testid="advertiser-payout-rules-operator"
									>
										{OPERATORS.map((o) => (
											<option key={o.value} value={o.value}>
												{o.label}
											</option>
										))}
									</select>
									<input
										type="number"
										step="any"
										className={INPUT}
										placeholder="value"
										value={draft.value}
										onChange={(e) =>
											setDraft((d) => ({ ...d, value: e.currentTarget.value }))
										}
										disabled={createMut.isPending}
										aria-label="Threshold value"
										data-testid="advertiser-payout-rules-value"
									/>
								</div>
							</div>

							<div className="flex flex-col gap-2 rounded-md border border-zinc-200 bg-zinc-50/60 p-3">
								<p className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
									Action
								</p>
								<div className="grid grid-cols-2 gap-2">
									<select
										className={SELECT}
										value={draft.action_kind}
										onChange={(e) =>
											setDraft((d) => ({
												...d,
												action_kind: e.currentTarget.value as PayoutRuleActionKind,
											}))
										}
										disabled={createMut.isPending}
										aria-label="Action kind"
										data-testid="advertiser-payout-rules-action-kind"
									>
										{ACTION_KINDS.map((k) => (
											<option key={k.value} value={k.value}>
												{k.label}
											</option>
										))}
									</select>
									<input
										type="number"
										step="any"
										className={INPUT}
										placeholder="amount"
										value={draft.action_amount}
										onChange={(e) =>
											setDraft((d) => ({
												...d,
												action_amount: e.currentTarget.value,
											}))
										}
										disabled={createMut.isPending}
										aria-label="Action amount"
										data-testid="advertiser-payout-rules-action-amount"
									/>
								</div>
								<p className="text-[11px] text-zinc-500">
									{ACTION_KINDS.find((k) => k.value === draft.action_kind)?.hint}
								</p>
							</div>

							<div className="flex flex-col gap-2 rounded-md border border-zinc-200 bg-zinc-50/60 p-3">
								<p className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
									Effective window
								</p>
								<div className="grid grid-cols-2 gap-2">
									<label className="flex flex-col gap-1 text-[11px] text-zinc-600">
										From
										<input
											type="date"
											className={INPUT}
											value={draft.effective_from}
											onChange={(e) =>
												setDraft((d) => ({
													...d,
													effective_from: e.currentTarget.value,
												}))
											}
											disabled={createMut.isPending}
											data-testid="advertiser-payout-rules-effective-from"
										/>
									</label>
									<label className="flex flex-col gap-1 text-[11px] text-zinc-600">
										To
										<input
											type="date"
											className={INPUT}
											value={draft.effective_to}
											onChange={(e) =>
												setDraft((d) => ({
													...d,
													effective_to: e.currentTarget.value,
												}))
											}
											disabled={createMut.isPending}
											data-testid="advertiser-payout-rules-effective-to"
										/>
									</label>
								</div>
								<p className="text-[11px] text-zinc-500">
									Leave empty for an always-on rule.
								</p>
							</div>

							<div className="flex flex-col gap-1.5">
								<label
									htmlFor="rule-status"
									className="text-xs font-medium text-zinc-700"
								>
									Status
								</label>
								<select
									id="rule-status"
									className={SELECT}
									value={draft.status}
									onChange={(e) =>
										setDraft((d) => ({
											...d,
											status: e.currentTarget.value as PayoutRuleStatus,
										}))
									}
									disabled={createMut.isPending}
									data-testid="advertiser-payout-rules-status"
								>
									<option value="active">Active</option>
									<option value="paused">Paused</option>
								</select>
							</div>

							{createMut.error ? (
								<p
									className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700"
									role="alert"
								>
									{createMut.error.message}
								</p>
							) : null}

							<div className="mt-auto flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
								<button
									type="button"
									className={BTN_SECONDARY}
									onClick={handleCloseDrawer}
									disabled={createMut.isPending}
								>
									Cancel
								</button>
								<button
									type="submit"
									className={BTN_PRIMARY}
									disabled={createMut.isPending || !canSubmit}
									data-testid="advertiser-payout-rules-submit"
								>
									{createMut.isPending ? "Saving." : "Create rule"}
								</button>
							</div>
						</form>
					</aside>
				</div>
			) : null}
		</main>
	);
}

export default AdvertiserPayoutRulesPage;
